import { router } from '@inertiajs/react';

export interface PendoAuthProps {
  user?: { id?: string | number | null } | null;
  company_id?: string | number | null;
}

export interface PendoIdentity {
  visitor: { id?: string };
  account: { id?: string };
}

const PENDO_METHODS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];

export function toPendoIdentity(auth?: PendoAuthProps | null): PendoIdentity {
  const userId = auth?.user?.id ? String(auth.user.id) : undefined;
  const companyId = auth?.company_id ? String(auth.company_id) : undefined;

  return {
    visitor: userId ? { id: userId } : {},
    account: companyId ? { id: companyId } : {},
  };
}

export function loadPendoAgent(publicAppId: string) {
  const w = window as any;
  const o = w.pendo = w.pendo || {};
  o._q = o._q || [];
  PENDO_METHODS.forEach((m) => {
    o[m] = o[m] || ((...args: unknown[]) => {
      o._q[m === 'initialize' ? 'unshift' : 'push']([m, ...args]);
    });
  });

  const scriptId = 'pendo-agent';
  const scriptSrc = `https://cdn.au.pendo.io/agent/static/${publicAppId}/pendo.js`;
  if (document.getElementById(scriptId)
    || Array.from(document.scripts).some((script) => script.src === scriptSrc)) {
    return;
  }

  const script = document.createElement('script');
  script.id = scriptId;
  script.async = true;
  script.src = scriptSrc;
  document.head.appendChild(script);
}

const identityKey = (identity: PendoIdentity) => `${identity.visitor.id ?? ''}|${identity.account.id ?? ''}`;

export function trackPendoNavigation(initialAuth?: PendoAuthProps | null) {
  // Inertia fires 'navigate' for the initial page load too; skip it so the
  // agent's own first pageLoad isn't double-counted.
  let skipInitialNavigate = true;
  let lastIdentity = identityKey(toPendoIdentity(initialAuth));

  router.on('navigate', (event) => {
    if (skipInitialNavigate) {
      skipInitialNavigate = false;
      return;
    }
    const pendo = (window as any).pendo;
    const identity = toPendoIdentity((event.detail.page.props as any)?.auth);
    if (identityKey(identity) !== lastIdentity) {
      lastIdentity = identityKey(identity);
      pendo?.identify?.(identity);
    }
    pendo?.pageLoad?.();
  });
}

export function setupPendo(publicAppId: string | undefined, initialAuth?: PendoAuthProps | null) {
  if (!publicAppId) {
    return;
  }
  loadPendoAgent(publicAppId);
  (window as any).pendo.initialize(toPendoIdentity(initialAuth));
  trackPendoNavigation(initialAuth);
}
